import React, { useState } from "react";
import { toast } from "react-toastify";
import Handler from "../../../Helpers/Handler";
import { RULES } from "../../../Helpers/bookingRules";
import { FaCloudUploadAlt } from "react-icons/fa";
import { ImCross } from "react-icons/im";
import tripUrls from "./tripUrls";

// End trip = the end-of-trip photos (Booking Flow v2, step 1 of ending a trip). The first save records
// the hand-back time, works out any late hours (free within the grace, then every started hour) and
// moves the booking Started -> Ended; the customer's pick-up code shows from then. While the booking is
// Ended the photos can be changed here; the hand-back time and the hours stay as first recorded.
// Photos already saved can be kept or removed (sent back as keepImages); new ones are appended.
// Next: Complete trip (odometer and fuel), then Fine charges.

const lateWords = (dueAt, graceMinutes) => {
    const due = new Date(dueAt).getTime();
    if (!Number.isFinite(due)) return null;
    const late = Math.ceil((Date.now() - due) / 60000);
    if (late <= 0) return "On time: no late hours.";
    if (late <= graceMinutes) return `${late} min past the return time, within the ${graceMinutes} min grace: no late hours.`;
    const hours = Math.ceil(late / 60);
    return `${late} min past the return time: ${hours} late hour${hours === 1 ? "" : "s"} will be charged.`;
};

const CloseTripModal = ({ bookingId, savedImages, minImages, maxImages, ended, dueAt, graceMinutes = 30, onClose, onClosed }) => {
    const MIN_IMAGES = Number(minImages) || RULES.TRIP_IMAGES_MIN;
    const MAX_IMAGES = Number(maxImages) || RULES.TRIP_IMAGES_MAX;
    const [saved, setSaved] = useState(savedImages || []); // links already on the booking
    const [imageTemp, setImageTemp] = useState([]); // [{imgId, file, url}] new this time
    const [submitting, setSubmitting] = useState(false);
    const total = saved.length + imageTemp.length;

    const imageChange = (e) => {
        const files = Object.values(e.target.files || {});
        if (!files.length) return;
        let room = MAX_IMAGES - total;
        if (files.length > room) toast.error(`Only ${MAX_IMAGES} photos are allowed; ${Math.max(room, 0)} more can be added`);
        files.forEach((file) => {
            if (room <= 0) return;
            if (!/^image\//.test(file.type)) { toast.error(`${file.name} is not an image`); return; }
            if (file.size <= RULES.IMAGE_MAX_BYTES) {
                room -= 1;
                setImageTemp((prev) => [...prev, { imgId: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, file, url: URL.createObjectURL(file) }]);
            } else {
                toast.error("Image must be below 10 MB");
            }
        });
        e.target.value = "";
    };

    const removeNew = (imgId) => setImageTemp((prev) => prev.filter((v) => v.imgId !== imgId));
    const removeSaved = (url) => setSaved((prev) => prev.filter((u) => u !== url));

    const submitEndTrip = async () => {
        if (total < MIN_IMAGES) { toast.error(`Upload at least ${MIN_IMAGES} end-of-trip photos (${total} so far)`); return; }
        const formData = new FormData();
        imageTemp.forEach((v) => formData.append("endImages", v.file));
        formData.append("keepImages", JSON.stringify(saved));

        setSubmitting(true);
        const response = await Handler({ method: "patch", url: tripUrls.close(bookingId), data: formData, formdata: true });
        setSubmitting(false);
        if (response?.success) {
            toast.success(ended ? "End photos updated" : "Trip ended. Next: Complete trip once the car is picked up");
            onClosed?.();
            onClose?.();
        } else {
            toast.error(response?.data?.message || "Could not end this trip");
        }
    };

    const late = ended ? null : lateWords(dueAt, Number(graceMinutes) || 0);

    return (
        <>
            <div className="modal fade show" style={{ display: "block" }} tabIndex="-1">
                <div className="modal-dialog modal-lg">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">{ended ? "Change End Photos" : "End Trip"}</h5>
                            <button type="button" className="btn-close" onClick={onClose} disabled={submitting}></button>
                        </div>
                        <div className="modal-body">
                            <div className="mb-2 small text-muted">
                                Upload photos of the car at hand-back ({MIN_IMAGES} to {MAX_IMAGES}; {total} added).
                                {ended
                                    ? " The hand-back time and the late hours stay as first recorded."
                                    : " The hand-back time is recorded when you save, and the trip moves to Ended. The odometer and fuel are entered next, in Complete trip, then the fine charges."}
                            </div>
                            {late && <div className="alert alert-light border py-2 px-3 small">{late}</div>}
                            <div className="d-flex flex-wrap gap-2 mb-3">
                                {saved.map((url) => (
                                    <div key={url} className="position-relative">
                                        <img src={url} alt="" width={84} height={84} className="rounded-3 border" style={{ objectFit: "cover" }} />
                                        <button type="button" className="btn btn-sm btn-danger p-0 rounded-circle position-absolute" style={{ top: -6, right: -6, width: 20, height: 20, lineHeight: "20px" }} onClick={() => removeSaved(url)} disabled={submitting} title="Remove">
                                            <ImCross size={8} />
                                        </button>
                                    </div>
                                ))}
                                {imageTemp.map((v) => (
                                    <div key={v.imgId} className="position-relative">
                                        <img src={v.url} alt="" width={84} height={84} className="rounded-3 border" style={{ objectFit: "cover" }} />
                                        <button type="button" className="btn btn-sm btn-danger p-0 rounded-circle position-absolute" style={{ top: -6, right: -6, width: 20, height: 20, lineHeight: "20px" }} onClick={() => removeNew(v.imgId)} disabled={submitting} title="Remove">
                                            <ImCross size={8} />
                                        </button>
                                    </div>
                                ))}
                                {total < MAX_IMAGES && (
                                    <label className="d-flex align-items-center justify-content-center rounded-3 border text-muted" style={{ width: 84, height: 84, cursor: submitting ? "not-allowed" : "pointer" }}>
                                        <FaCloudUploadAlt size={20} />
                                        <input type="file" multiple accept="image/png,image/jpeg,image/webp" className="d-none" onChange={imageChange} disabled={submitting} />
                                    </label>
                                )}
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={submitting}>Cancel</button>
                            <button type="button" className="btn btn-success" onClick={submitEndTrip} disabled={submitting || total < MIN_IMAGES}>
                                {submitting ? "Saving..." : ended ? "Save Photos" : "End Trip"}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <div className="modal-backdrop fade show"></div>
        </>
    );
};

export default CloseTripModal;
